import { createRoot } from "react-dom/client";
import { configure } from "mobx";
import { USERPILOT_ACCOUNT_ID } from "utils/config";
import { ENV } from "utils/hostEnv";
import {
  IssueReporter,
  isKnownBenignException,
  isTransientFetchFailure,
} from "utils/issueReporter";
import Cookies from "js-cookie";
import { stores } from "./stores";
import { Userpilot } from "userpilot";
import { ColorModeScript } from "@chakra-ui/react";
import { chakraTheme } from "theme";
import { printSha, swapFaviconForEnv } from "utils/utilFunctions";
import posthog from "posthog-js";

// Self-hosted fonts (previously Google Fonts) — keep in sync with
// .storybook/preview.tsx. The latin subset is preloaded at build time by the
// preload-font-links plugin in vite.config.ts.
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";

// This needs to be imported here to avoid a conflict with the global CSS
import "@timescale/popsql-query-widget/index.css";
import { statsig } from "utils/statsig";
import { App } from "./App";
import { router } from "./router";
import { StrictMode } from "react";

swapFaviconForEnv();
printSha(); // Print the SHA for GitHub Action builds

// MobX strict mode and debugging
configure({ enforceActions: "always", reactionRequiresObservable: true });
window._____APP_STATE_____ = stores;

// Initialize logging and analytics as early as possible for speed.
// Release `version+sha` must match scripts/sentry.mjs, or source maps
// won't symbolicate.
const sha = import.meta.env.VITE_SHA;
IssueReporter.init({
  dsn: ENV.SENTRY,
  environment: ENV.NAME,
  release: sha ? `${__APP_VERSION__}+${sha}` : "NO_RELEASE_FOUND",
  router,
});

// Analytics/telemetry are best-effort: a failure here must never block app
// mount. Report failures to Sentry but keep going.
const initOptional = (name: string, fn: () => void) => {
  try {
    fn();
  } catch (error) {
    IssueReporter.error(`${name} init failed`, { error: error as Error });
  }
};

initOptional("posthog", () =>
  posthog.init(ENV.POSTHOG_CONFIG.PUBLIC_KEY, {
    api_host: ENV.POSTHOG_CONFIG.PUBLIC_HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    before_send: (event) => {
      if (event?.event === "$exception") {
        // Don't ship exceptions from local dev — they're noisy and not
        // actionable. Deployed envs (dev, prod) still report.
        if (import.meta.env.DEV) return null;

        const list = event.properties?.$exception_list as
          | Array<{ type?: string; value?: string }>
          | undefined;
        if (
          list?.some(
            (e) =>
              isKnownBenignException(e.type, e.value) ||
              isTransientFetchFailure(e.value),
          )
        ) {
          return null;
        }
      }
      return event;
    },
  }),
);

statsig
  .initializeAsync()
  .catch((error) => IssueReporter.error("statsig init failed", { error }));

initOptional("userpilot", () => {
  Userpilot.initialize(USERPILOT_ACCOUNT_ID);
  if (new URLSearchParams(window.location.search).has("up")) {
    Userpilot.anonymous();
  }
});

// Remove residual cookie for verify route as early as possible
if (/verify\/?$/.test(window.location.pathname)) {
  Cookies.remove("_TS");
}

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ColorModeScript initialColorMode={chakraTheme.config.initialColorMode} />
      <App />
    </StrictMode>,
  );
}

// If this entry module is ever hot-updated (e.g. an HMR chain propagates
// up to it), force a full reload instead of re-executing the bootstrap
// in place. Re-running side effects like Sentry/Posthog/Statsig init and
// `createRoot` against an already-mounted tree is what produced the race
// this file is structured to avoid.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    import.meta.hot!.invalidate();
  });
}
